// MegaMenu.jsx
import React, { useState, useEffect, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { __, lang } from '../../Lang.js';

const MegaMenu = ({ isOpen, onClose }) => {
  const [menuData, setMenuData] = useState([]);
  const [endpointPrefix, setEndpointPrefix] = useState('/api/');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      fetch(`/api/v2/twi/navigation`)
        .then(response => {
          if (!response.ok) throw new Error('Failed to fetch navigation data');
          return response.json();
        })
        .then(data => {
          setMenuData(data.navigation);
          setEndpointPrefix(data.endpointPrefix || '/api/');
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Error fetching navigation data:', err);
          setError(err.message);
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.classList.contains('mega-menu__close-btn')) {
        onClose();
        return;
      }
      if (menuRef.current && !menuRef.current.contains(event.target) && !event.target.closest('[data-megamenu-trigger]')) {
        onClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      if (window.innerWidth < 768) {
        document.body.style.overflow = 'hidden';
        document.body.classList.add('mega-menu-open');
      }
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      if (window.innerWidth < 768) {
        document.body.style.overflow = '';
        document.body.classList.remove('mega-menu-open');
      }
    };
  }, [isOpen, onClose]);

  const formatLink = (endpoint) => {
    if (!endpoint) return '#';
    return endpoint.replace(endpointPrefix, '/app/');
  };

  const customPlaylists = [];
  const coreNav = [];

  menuData.forEach(item => {
    if (item.sections) {
      const playlistSections = [];
      const otherSections = [];

      item.sections.forEach(section => {
        const url = formatLink(section.endpoint);
        if (section.type == 'mylist' || url.includes('/mylist')) {
          playlistSections.push(section);
        } else {
          otherSections.push(section);
        }
      });

      if (playlistSections.length) {
        customPlaylists.push(...playlistSections);
      }

      if (otherSections.length || !playlistSections.length) {
        coreNav.push({ ...item, sections: otherSections });
      }
    } else {
      coreNav.push(item);
    }
  });

  const transformPlaylistName = (name) => {
    // Deprecated - this should be correct coming from the API
    return name;
  };

const column1 = [
  {
    title: __('My Lists'),
    sections: customPlaylists 
  },
  {
    title: __('Playlists'),
    sections: [
      { name: __('All Playlists'), endpoint: '/app/twi/playlists' },
      { name: __('Audiobooks'), endpoint: '/app/twi/playlists?is_audiobook=1' },
      { name: __('Collections'), endpoint: '/app/twi/playlists?is_collection=1' },
    ]
  }
];

  // Match by key titles that could be in any language - use flexible matching
  const matchTitle = (group, possibleTitles) => possibleTitles.some(t =>
    group.title?.toLowerCase().includes(t.toLowerCase()) || t.toLowerCase().includes(group.title?.toLowerCase())
  );

  const topics = coreNav.find(g => matchTitle(g, ['topic', 'tema', 'tópico']));
  const topicSections = topics?.sections || [];
  const firstChunk = topicSections.slice(0, 15);
  const secondChunk = topicSections.slice(15);

  const column2 = coreNav.filter(g =>
    matchTitle(g, ['favorite', 'favorito']) || matchTitle(g, ['initiate', 'iniciado', 'minister', 'ministro'])
  );

  const extraEsSAT = coreNav.find(g => matchTitle(g, ['biblioteca', 'português', 'portuguese']));

  const satGroup = coreNav.find(g => matchTitle(g, ['sat seminar', 'seminario', 'seminário']));

  const column3 = [
    {
      title: topics?.title || __('Topics'),
      sections: firstChunk
    },
    {
      title: satGroup?.title || __('SAT Seminars'),
      sections: [
        ...(satGroup?.sections || []),
        ...(extraEsSAT ? [{ name: extraEsSAT.title, endpoint: formatLink(extraEsSAT.endpoint || extraEsSAT.link) }] : [])
      ]
    }
  ];

  const extraPtCollections = coreNav.find(g => matchTitle(g, ['coleções', 'coleçõe']));
  const languageGroup = coreNav.find(g => matchTitle(g, ['language', 'idioma', 'língua']));

  const column4 = [
    {
      title: __('Topics (Cont.)'),
      sections: secondChunk
    },
    {
      title: languageGroup?.title || __('Language'),
      sections: languageGroup?.sections || [],
    },
  {
    title: 'More',
    sections: [
      { name: __('Recently Added'), endpoint: '/app/twi/results?type=recent' },
      { name: __('New to Me'), endpoint: '/app/twi/results?type=unplayed' },
      { name: __('Continue Watching'), endpoint: '/app/twi/results?type=continue' },
      { name: __('Frequently Watched'), endpoint: '/app/twi/results?type=heavy' },
      { name: __('History'), endpoint: '/app/twi/results?type=history' },
      ...(extraPtCollections ? [{
        name: extraPtCollections.title,
        endpoint: formatLink(extraPtCollections.endpoint || extraPtCollections.link)
      }] : [])
    ]
  }
];

  if (!isOpen) return null;

  return (
    <div 
      ref={menuRef} 
      className="mega-menu"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <IconButton className="mega-menu__close-btn" onClick={onClose} aria-label="Close menu">✕</IconButton>

      {isLoading && <div className="mega-menu__loading">{ __('Loading') }...</div>}
      {error && <div className="mega-menu__error">{error}</div>}
      {!isLoading && !error && (
        <div className="mega-menu__scroll">
          <div
              className="mega-menu__grid"
              style={{
                display: window.innerWidth < 768 ? 'flex' : undefined,
                flexDirection: window.innerWidth < 768 ? 'column' : undefined,
                gap: window.innerWidth < 768 ? '1.5rem' : undefined
              }}
            >
            {[column1, column2, column3, column4].map((col, colIndex) => (
              <div key={`col-${colIndex}`}>
                {col.map((item, idx) => (
                  <div key={`item-${colIndex}-${idx}`} className="mega-menu__section">
                    <div className="mega-menu__title">{item.title}</div>
                    {item.sections && item.sections.map((section, j) => (
                      <Link key={j} href={formatLink(section.endpoint)} className="mega-menu__link" color="inherit" underline="hover">{section.name}</Link>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MegaMenu;

export const DiscoverWithMegaMenu = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const buttonRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="mega-menu__trigger-wrapper">
<Link
  href="/app/twi/discover"
  ref={buttonRef}
  data-megamenu-trigger
  className="internal-nav__link"
  color="inherit"
  underline="none"
  style={{
    color: window.innerWidth < 768 ? '#222' : 'white',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  }}
  onClick={(e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsMenuOpen(prev => !prev);
  }}
  onMouseEnter={() => {
    if (!isMobile) {
      setIsMenuOpen(true);
    }
  }}
>
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"></rect>
    <rect x="14" y="3" width="7" height="7"></rect>
    <rect x="14" y="14" width="7" height="7"></rect>
    <rect x="3" y="14" width="7" height="7"></rect>
  </svg>
  {__('Discover')}
</Link>
      <MegaMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </div>
  );
};
